import React, { useState } from "react";
import { Box, Stack } from "@mui/material";
import Button from "@mui/material/Button";
import TabPanel from "@mui/lab/TabPanel";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import { retrievePausedOrders } from "./selector";
import { Product } from "../../../lib/types/product";
import { Messages, serverApi } from "../../../lib/config";
import { Order, OrderItem, OrderUpdateInput } from "../../../lib/types/order";
import { T } from "../../../lib/types/common";
import { sweetErrorHandling } from "../../../lib/sweetAlert";
import { OrderStatus } from "../../../lib/enums/order.enum";
import { useGlobals } from "../../hooks/useGlobals";
import OrderService from "../../services/OrderService";

/** REDUX SLICE & SELECTOR */
const pausedOrdersRetriever = createSelector(
    retrievePausedOrders,
    (pausedOrders) => ({ pausedOrders })
);

function formatOrderDate(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function getFirstProduct(order: Order): Product | undefined {
  if (!order?.orderItems?.length || !order?.productData?.length) return undefined;
  const firstItem: OrderItem = order.orderItems[0];
  return order.productData.find((p: Product) => p._id === firstItem.productId);
}

function getOrderItemCount(order: Order): number {
  return order?.orderItems?.length ?? 0;
}

function getProductForItem(order: Order, item: OrderItem): Product | undefined {
  return order.productData?.find((p: Product) => p._id === item.productId);
}

interface PausedOrdersProps {
  setValue: (input: string) => void
}

export default function PausedOrders(props: PausedOrdersProps) {
  const { setValue } = props;
  const {authMember, setOrderBuilder} = useGlobals();
  const {pausedOrders} = useSelector(pausedOrdersRetriever);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);

  const toggleViewDetails = (orderId: string) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  };

  const deleteOrderHandler = async (e: T) => {
    try{
      if(!authMember) throw new Error(Messages.error2);
      const orderid = (e.target as HTMLButtonElement).value;
      const input: OrderUpdateInput = {
        orderId: orderid,
        orderStatus: OrderStatus.DELETE
      };

      const confirmation = window.confirm("Do you want to cancel the order?");
      if(confirmation) {
        const order = new OrderService();
        await order.updateOrder(input);
        setOrderBuilder(new Date());
      }

    }catch(err) {
      console.log(err);
      sweetErrorHandling(err).then()
    }
  }

  const processOrderHandler = async (e: T) => {
    try {
      if (!authMember) throw new Error(Messages.error2);
      const address = authMember.memberAdress?.trim();
      if (!address) {
        await sweetErrorHandling({ message: "Address not provided. Please provide your address." });
        return;
      }
      const orderid = (e.target as HTMLButtonElement).value;
      const input: OrderUpdateInput = {
        orderId: orderid,
        orderStatus: OrderStatus.PROCESS
      };

      const confirmation = window.confirm("Do you want to proceed with payment?");
      if (confirmation) {
        const order = new OrderService();
        await order.updateOrder(input);
        setValue("2");
        setOrderBuilder(new Date());
      }
    } catch (err) {
      console.log(err);
      sweetErrorHandling(err).then();
    }
  };

  return (
    <TabPanel value={"1"}>
      <Stack className="paused-orders-stack">
        {pausedOrders.map((order: Order) => {
          const firstProduct = getFirstProduct(order);
          const itemCount = getOrderItemCount(order);
          const orderIdShort = order._id.slice(-5);

          if (!firstProduct) return null;

          const imagePath = firstProduct.productImages?.length
            ? `${serverApi}/${firstProduct.productImages[0]}`
            : "/icons/noimage-list.svg";

          const otherItems = order.orderItems?.slice(1) ?? [];
          const isExpanded = expandedOrderId === order._id;
          const hasOtherProducts = otherItems.length > 0;

          return (
            <Box key={order._id} className="paused-order-card">
              <Box className="paused-order-card-body">
                <img
                  src={imagePath}
                  alt={firstProduct.productName}
                  className="paused-order-product-img"
                />
                <Box className="paused-order-content">
                  <Box className="paused-order-card-top">
                    <span className="paused-order-badge">PAYMENT REQUIRED</span>
                    <span className="paused-order-id">Order #{orderIdShort}</span>
                  </Box>
                  <Box className="paused-order-price-row">
                    <p className="paused-order-total">${order.orderTotal.toFixed(2)}</p>
                    <p className="paused-order-items">
                      {itemCount === 1 ? "(1 book)" : `(${itemCount} books)`}
                    </p>
                  </Box>
                  <h3 className="paused-order-product-title">{firstProduct.productName}</h3>
                  <p className="paused-order-product-author">
                    {firstProduct.productAuthor || "No author"}
                  </p>
                  <Box className="paused-order-date-row">
                    <AccessTimeIcon className="paused-order-date-icon" />
                    <span>Placed on {formatOrderDate(order.createdAt)}</span>
                  </Box>
                </Box>
              </Box>

              <Box className="paused-order-divider" />

              {isExpanded && hasOtherProducts && (
                <Box className="paused-order-details-list">
                  {order.orderItems.map((item: OrderItem) => {
                    const product = getProductForItem(order, item);
                    if (!product) return null;
                    const itemImg = product.productImages?.length
                      ? `${serverApi}/${product.productImages[0]}`
                      : "/icons/noimage-list.svg";
                    return (
                      <Box key={item._id} className="paused-order-detail-row">
                        <img src={itemImg} alt={product.productName} className="paused-order-detail-img" />
                        <Box className="paused-order-detail-info">
                          <span className="paused-order-detail-name">{product.productName}</span>
                          {product.productAuthor && (
                            <p className="paused-order-detail-author">{product.productAuthor}</p>
                          )}
                          <span className="paused-order-detail-qty">
                            {item.itemQuantity} × ${item.itemPrice.toFixed(2)} = ${(item.itemQuantity * item.itemPrice).toFixed(2)}
                          </span>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}

              <Box className="paused-order-footer-actions">
                {hasOtherProducts && (
                  <Button
                    variant="outlined"
                    className="paused-order-view-details"
                    onClick={() => toggleViewDetails(order._id)}
                  >
                    {isExpanded ? "Hide details" : "View details"}
                  </Button>
                )}
                <Button
                  value={order._id}
                  variant="outlined"
                  color="secondary"
                  className="paused-order-cancel-button"
                  onClick={deleteOrderHandler}
                >
                  Cancel
                </Button>
                <Button
                  value={order._id}
                  variant="contained"
                  className="paused-order-pay-button"
                  onClick={processOrderHandler}
                  startIcon={<CreditCardIcon />}
                >
                  Pay Now
                </Button>
              </Box>
            </Box>
          );
        })}

        {(!pausedOrders || pausedOrders.length === 0) && (
          <Box display="flex" flexDirection="row" justifyContent="center">
            <img
              src="/icons/noimage-list.svg"
              style={{ width: 300, height: 300 }}
              alt="No orders"
            />
          </Box>
        )}
      </Stack>
    </TabPanel>
  );
}
